import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircleIcon, ArrowLeftIcon, PlusIcon, VoteIcon, BrainIcon, LockIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { createSnap } from '@/lib/api';
import type { SnapCategory } from '@/types/database.types';
import FormSection from '@/components/form/FormSection';
import FormField from '@/components/form/FormField';
import Input from '@/components/form/Input';
import Button from '@/components/form/Button';
import CategorySelect from '@/components/form/CategorySelect';
import DurationSlider from '@/components/form/DurationSlider';
import Toggle from '@/components/form/Toggle';
import OptionField from '@/components/form/OptionField';
import EnhanceTextModal from '@/components/snap/EnhanceTextModal';
import GenerateOptionModal from '@/components/snap/GenerateOptionModal';
import { getTempImage, isTempImageUrl, clearTempImages } from '@/components/common/TempImageStorage';
import { uploadSnapImage } from '@/lib/supabase-storage';

interface SnapOption {
  title: string;
  description: string;
  imageUrl: string;
}

const defaultOption: SnapOption = {
  title: '',
  description: '',
  imageUrl: '',
};

const MAX_OPTIONS = 5;

export default function CreateSnap() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<SnapCategory[]>([]);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [durationDays, setDurationDays] = useState(7);
  const [options, setOptions] = useState<SnapOption[]>([
    { ...defaultOption },
    { ...defaultOption },
  ]);
  const [showEnhanceModal, setShowEnhanceModal] = useState(false);
  const [showGenerateOptionModal, setShowGenerateOptionModal] = useState(false);

  const handleOptionChange = (index: number, field: keyof SnapOption, value: string) => {
    setOptions(prev => 
      prev.map((opt, i) => 
        i === index ? { ...opt, [field]: value } : opt
      )
    );
  };

  const addOption = () => {
    if (options.length < MAX_OPTIONS) {
      setOptions(prev => [...prev, { ...defaultOption }]);
    }
  };

  const removeOption = (index: number) => {
    if (options.length > 2) {
      setOptions(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Please enter a title for your SNAP');
      return;
    }

    if (selectedCategories.length === 0) {
      toast.error('Please select at least one category');
      return;
    }

    if (options.some(opt => !opt.title.trim())) {
      toast.error('Please provide a title for each option');
      return;
    }

    try {
      setLoading(true);
      
      // Process any temporary images before submitting
      const processedOptions = await Promise.all(options.map(async (option, index) => {
        let finalImageUrl = option.imageUrl;
        
        // If the image URL is a temporary blob URL, upload it to Supabase
        if (option.imageUrl && isTempImageUrl(option.imageUrl)) {
          const tempId = `option-${index}`;
          const blob = getTempImage(tempId);
          
          if (blob) {
            // Convert blob to file
            const file = new File([blob], `option-${index}-${Date.now()}.jpg`, { type: 'image/jpeg' });
            
            // Upload to Supabase
            const { url, error } = await uploadSnapImage(file);
            
            if (error || !url) {
              throw new Error(`Failed to upload image for option ${index}`);
            }
            
            finalImageUrl = url;
          } else {
            finalImageUrl = '';
          }
        }
        
        return {
          title: option.title,
          description: option.description,
          imageUrl: finalImageUrl
        };
      }));
      
      // Create the SNAP with the processed options
      await createSnap(title, description, isAnonymous, durationDays, selectedCategories, processedOptions, isPrivate);
      
      // Clear all temporary images
      clearTempImages();
      
      toast.success('SNAP created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to create SNAP');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnhancedText = (enhancedQuestion: string, enhancedDescription: string) => {
    setTitle(enhancedQuestion);
    setDescription(enhancedDescription);
    toast.success('Text enhanced successfully!');
  };

  const handleGeneratedOption = (newTitle: string, newDescription: string) => {
    if (options.length >= MAX_OPTIONS) {
      toast.error(`Maximum of ${MAX_OPTIONS} options allowed`);
      return;
    }
    setOptions(prev => [...prev, { title: newTitle, description: newDescription, imageUrl: '' }]);
    toast.success('AI option added successfully!');
  };

  return (
    <div className="max-w-4xl mx-auto px-2 sm:px-0">
      <Button
        variant="ghost"
        size="sm"
        icon={<ArrowLeftIcon className="h-4 w-4" />}
        onClick={() => {
          clearTempImages(); // Clean up any temporary images when navigating away
          navigate('/dashboard');
        }}
        className="mb-4 sm:mb-6"
      >
        Back to Dashboard
      </Button>

      <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-4 sm:px-8 py-4 sm:py-6 bg-gradient-to-r from-primary-600 to-primary-700">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Create a New SNAP</h1>
          <p className="mt-2 text-primary-100">Get community input on your decisions</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 sm:p-8 space-y-6 sm:space-y-8">
          <FormSection title="Basic Information">
            <FormField 
              label="Question or Decision" 
              required
              helpText="What do you want the community to decide?"
            >
              <Input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter your question or decision"
                required
              />
            </FormField>

            <FormField 
              label="Description" 
              optional
              helpText="Provide more context about your question"
            >
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="block w-full px-4 py-3 rounded-lg border border-gray-300 bg-white shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50"
                placeholder="Add more details to help others understand your question..."
              />
            </FormField>

            <div className="mt-2">
              <Button
                type="button"
                variant="outline"
                icon={<BrainIcon className="h-4 w-4" />}
                onClick={() => setShowEnhanceModal(true)}
                disabled={!title.trim()}
                className="w-full sm:w-auto"
              >
                Enhance with AI
              </Button>
            </div>

            <FormField label="Categories" required>
              <CategorySelect
                selectedCategories={selectedCategories}
                onChange={setSelectedCategories}
                maxCategories={3}
              />
            </FormField>
          </FormSection>

          <FormSection title="Options">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Options</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                icon={<BrainIcon className="h-4 w-4" />}
                onClick={() => setShowGenerateOptionModal(true)}
                disabled={!title.trim() || selectedCategories.length === 0 || options.length >= MAX_OPTIONS}
              >
                Option by AI
              </Button>
            </div>

            <div className="space-y-4 sm:space-y-6">
              {options.map((option, index) => (
                <OptionField
                  key={index}
                  index={index}
                  title={option.title}
                  description={option.description}
                  imageUrl={option.imageUrl}
                  onTitleChange={(value) => handleOptionChange(index, 'title', value)}
                  onDescriptionChange={(value) => handleOptionChange(index, 'description', value)}
                  onImageUrlChange={(value) => handleOptionChange(index, 'imageUrl', value)}
                  onRemove={() => removeOption(index)}
                  canRemove={options.length > 2}
                />
              ))}
            </div>
            
            {options.length < MAX_OPTIONS && (
              <Button
                type="button"
                variant="outline"
                icon={<PlusIcon className="h-5 w-5" />}
                onClick={addOption}
                className="mt-4 sm:mt-6 w-full border-2 border-dashed"
              >
                Add Another Option
              </Button>
            )}

            <div className="mt-4 flex items-center p-3 sm:p-4 bg-gray-50 rounded-lg">
              <AlertCircleIcon className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
              <p className="text-sm text-gray-600">
                You can add up to {MAX_OPTIONS} options. Each option should represent a distinct choice.
              </p>
            </div>
          </FormSection>

          <FormSection title="Settings">
            <FormField
              label="Duration (days)"
              helpText={`Your SNAP will be active for ${durationDays} days`}
            >
              <DurationSlider value={durationDays} onChange={setDurationDays} />
            </FormField>

            <div className="space-y-4">
              <Toggle
                id="private"
                checked={isPrivate}
                onChange={setIsPrivate}
                label="Make this SNAP private"
                description="Only invited users can see and participate in this SNAP"
              />

              <Toggle
                id="anonymous"
                checked={isAnonymous}
                onChange={setIsAnonymous}
                label="Post anonymously"
                description="Your username won't be visible to other users"
              />
            </div>
          </FormSection>

          <div className="flex justify-end space-x-4 pt-4 sm:pt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                clearTempImages(); // Clean up any temporary images
                navigate('/dashboard');
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={loading}
              icon={<VoteIcon className="h-4 w-4" />}
            >
              Create SNAP
            </Button>
          </div>
        </form>
      </div>

      <EnhanceTextModal
        isOpen={showEnhanceModal}
        onClose={() => setShowEnhanceModal(false)}
        question={title}
        description={description}
        onAccept={handleEnhancedText}
      />

      <GenerateOptionModal
        isOpen={showGenerateOptionModal}
        onClose={() => setShowGenerateOptionModal(false)}
        question={title}
        description={description}
        categories={selectedCategories}
        onAccept={handleGeneratedOption}
      />
    </div>
  );
}